// The fewest small edits that turn one text into another, for a whole-text replacement made through the editor (Fix
// quotes, Clean up, Reject All on an open fragment): as Automerge's `updateText` does for the store, only what changed
// is replaced, so comment anchors and revision marks elsewhere — even between two changes in one paragraph — hold.
//
// The texts' common ends are trimmed; the lines between are aligned (Myers, the core's `difference`); each run of
// changed lines is then diffed by character when it's short enough to be cheap, else replaced as its differing middle.
// A surrogate pair is never split.
import { difference } from '@writing-suite/core';

export interface TextEdit {
  /** UTF-16 [from, to) in the old text. */
  from: number;
  to: number;
  insert: string;
}

/** A run of changed text this long or shorter is diffed character by character. */
const CHARACTER_DIFF_LIMIT = 3_000;

const isTrail = (s: string, i: number) => i > 0 && i < s.length && (s.charCodeAt(i) & 0xfc00) === 0xdc00;

/** The edits taking `a` to `b`, in ascending order, never overlapping (offsets in `a`). */
export function textEdits(a: string, b: string): TextEdit[] {
  if (a === b) return [];
  let prefix = 0;
  const max = Math.min(a.length, b.length);
  while (prefix < max && a.charCodeAt(prefix) === b.charCodeAt(prefix)) prefix++;
  while (prefix > 0 && (isTrail(a, prefix) || isTrail(b, prefix))) prefix--;
  let suffix = 0;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a.charCodeAt(a.length - 1 - suffix) === b.charCodeAt(b.length - 1 - suffix)) suffix++;
  while (suffix > 0 && (isTrail(a, a.length - suffix) || isTrail(b, b.length - suffix))) suffix--;
  const oldMid = a.slice(prefix, a.length - suffix), newMid = b.slice(prefix, b.length - suffix);
  const out: TextEdit[] = [];
  for (const e of lineEdits(oldMid, newMid)) out.push({ from: e.from + prefix, to: e.to + prefix, insert: e.insert });
  return out;
}

/** Lines with their newline, so joining them gives the text back. */
function lines(s: string): string[] {
  const out: string[] = [];
  let at = 0;
  for (;;) {
    const nl = s.indexOf('\n', at);
    if (nl < 0) { if (at < s.length) out.push(s.slice(at)); return out; }
    out.push(s.slice(at, nl + 1));
    at = nl + 1;
  }
}

function lineEdits(a: string, b: string): TextEdit[] {
  if (a === b) return [];
  if (!a.length || !b.length) return [{ from: 0, to: a.length, insert: b }];
  const la = lines(a), lb = lines(b);
  // Offsets of each old line.
  const starts: number[] = [];
  let at = 0;
  for (const l of la) { starts.push(at); at += l.length; }
  starts.push(at);
  const removed = new Set<number>(), inserted = new Set<number>();
  for (const c of difference(la, lb)) (c.type === 'remove' ? removed : inserted).add(c.offset);
  const out: TextEdit[] = [];
  let i = 0, j = 0;
  while (i < la.length || j < lb.length) {
    if ((i < la.length && removed.has(i)) || (j < lb.length && inserted.has(j))) {
      const oi = i, oj = j;
      while (i < la.length && removed.has(i)) i++;
      while (j < lb.length && inserted.has(j)) j++;
      const from = starts[oi], to = starts[i];
      const insert = lb.slice(oj, j).join('');
      for (const e of runEdits(a.slice(from, to), insert)) out.push({ from: from + e.from, to: from + e.to, insert: e.insert });
    } else {
      i++;
      j++;
    }
  }
  return out;
}

/** One run of changed lines: by character when short, else its differing middle. */
function runEdits(a: string, b: string): TextEdit[] {
  if (a === b) return [];
  if (!a.length || !b.length || a.length + b.length > CHARACTER_DIFF_LIMIT) return middle(a, b);
  const ca = [...a], cb = [...b]; // code points, so a pair is never split
  const offsets: number[] = [];
  let at = 0;
  for (const c of ca) { offsets.push(at); at += c.length; }
  offsets.push(at);
  const removed = new Set<number>(), inserted = new Set<number>();
  for (const c of difference(ca, cb)) (c.type === 'remove' ? removed : inserted).add(c.offset);
  const out: TextEdit[] = [];
  let i = 0, j = 0;
  while (i < ca.length || j < cb.length) {
    if ((i < ca.length && removed.has(i)) || (j < cb.length && inserted.has(j))) {
      const oi = i, oj = j;
      while (i < ca.length && removed.has(i)) i++;
      while (j < cb.length && inserted.has(j)) j++;
      out.push({ from: offsets[oi], to: offsets[i], insert: cb.slice(oj, j).join('') });
    } else {
      i++;
      j++;
    }
  }
  return out;
}

function middle(a: string, b: string): TextEdit[] {
  let p = 0;
  const max = Math.min(a.length, b.length);
  while (p < max && a.charCodeAt(p) === b.charCodeAt(p)) p++;
  while (p > 0 && (isTrail(a, p) || isTrail(b, p))) p--;
  let s = 0;
  while (s < a.length - p && s < b.length - p && a.charCodeAt(a.length - 1 - s) === b.charCodeAt(b.length - 1 - s)) s++;
  while (s > 0 && (isTrail(a, a.length - s) || isTrail(b, b.length - s))) s--;
  return [{ from: p, to: a.length - s, insert: b.slice(p, b.length - s) }];
}

/**
 * The edits taking `a` to `b` that never replace a character the reader sees which both keep: the visible characters
 * (`visibleA`/`visibleB` say which are) are aligned first, then the markup between each aligned pair is diffed. A plain
 * character diff would rather keep six marker characters than five words' letters — `ijk67]{.sc}` to `]{.sc}ijk67`
 * would delete the letters and type them again — and a co-writer's edit to those letters would then bring them back
 * twice (W9b). Offsets in `a`, ascending.
 */
export function visibleEdits(a: string, b: string, visibleA: (i: number) => boolean, visibleB: (i: number) => boolean): TextEdit[] {
  if (a === b) return [];
  // Visible characters as whole code points (aligned by scalar, as the Swift does): a pair's halves are compared and
  // anchored together, so "😀" → "😃" (one high surrogate, two low ones) never becomes an edit between the halves.
  const points = (s: string, visible: (i: number) => boolean) => {
    const at: number[] = [], chars: string[] = [];
    for (let i = 0; i < s.length;) {
      const n = s.codePointAt(i)! > 0xffff ? 2 : 1;
      if (visible(i)) { at.push(i); chars.push(s.slice(i, i + n)); }
      i += n;
    }
    return { at, chars };
  };
  const pa_ = points(a, visibleA), pb_ = points(b, visibleB);
  const va = pa_.at, vb = pb_.at, ca = pa_.chars, cb = pb_.chars;
  const removed = new Set<number>(), inserted = new Set<number>();
  for (const c of difference(ca, cb)) (c.type === 'remove' ? removed : inserted).add(c.offset);
  const anchors: [number, number, number][] = [];
  let i = 0, j = 0;
  while (i < ca.length && j < cb.length) {
    if (removed.has(i)) { i++; continue; }
    if (inserted.has(j)) { j++; continue; }
    anchors.push([va[i], vb[j], ca[i].length]);
    i++;
    j++;
  }
  const out: TextEdit[] = [];
  let pa = 0, pb = 0;
  const gap = (ea: number, eb: number) => {
    for (const e of textEdits(a.slice(pa, ea), b.slice(pb, eb))) out.push({ from: e.from + pa, to: e.to + pa, insert: e.insert });
  };
  for (const [x, y, n] of anchors) {
    gap(x, y);
    pa = x + n;
    pb = y + n;
  }
  gap(a.length, b.length);
  return out;
}
